// ABOUTME: Renders audited WhatsApp pre-registration store links and QR codes.
// ABOUTME: Exports only server-issued wa.me URLs and never handles raw entry tokens.
import React from "react";
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import BlockIcon from "@material-ui/icons/Block";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import RefreshIcon from "@material-ui/icons/Refresh";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { QRCodeSVG } from "qrcode.react";

import { WhatsappPreRegistrationLabels } from "./whatsappPreRegistrationLabels";
import {
    buildWhatsappPreRegistrationCsv,
    getWhatsappPreRegistrationLinkPresentation,
    getWhatsappPreRegistrationLinkSharingState,
    getWhatsappPreRegistrationSelectedQrUrl,
    mergeWhatsappPreRegistrationLinkRows,
    WhatsappPreRegistrationConfig,
    WhatsappPreRegistrationGeneralEntry,
    WhatsappPreRegistrationOptions
} from "./whatsappPreRegistrationSettingsModel";

interface WhatsappPreRegistrationLinksProps {
    config: WhatsappPreRegistrationConfig;
    generalEntry: WhatsappPreRegistrationGeneralEntry;
    featureActive: boolean;
    stores: WhatsappPreRegistrationOptions["stores"];
    links: any[];
    actingStoreId: string;
    actionsDisabled: boolean;
    labels: WhatsappPreRegistrationLabels;
    onRotate: (storeId: string, hasCurrentLink: boolean) => void;
    onRevoke: (storeId: string) => void;
    onNotice: (text: string) => void;
    onError: (error: unknown) => void;
}

const ServerUrlQrCode = QRCodeSVG as any;

const WhatsappPreRegistrationLinks = ({
    config,
    generalEntry,
    featureActive,
    stores,
    links,
    actingStoreId,
    actionsDisabled,
    labels,
    onRotate,
    onRevoke,
    onNotice,
    onError
}: WhatsappPreRegistrationLinksProps) => {
    const [selectedQrUrl, setSelectedQrUrl] = React.useState("");
    const rows = React.useMemo(
        () => mergeWhatsappPreRegistrationLinkRows({ config, stores, links }),
        [config, links, stores]
    );
    const sharingState = getWhatsappPreRegistrationLinkSharingState({
        actionsDisabled,
        featureActive,
        generalEntry,
        rows,
        selectedQrUrl
    });
    const hasUsableGeneralEntry =
        Boolean(sharingState.generalEntryUrl);
    const currentSelectedQrUrl = getWhatsappPreRegistrationSelectedQrUrl(
        selectedQrUrl,
        sharingState.selectedQrUrl
    );

    React.useEffect(() => {
        if (selectedQrUrl !== currentSelectedQrUrl) {
            setSelectedQrUrl(currentSelectedQrUrl);
        }
    }, [currentSelectedQrUrl, selectedQrUrl]);

    const copyLink = async (waMeUrl: string) => {
        if (!sharingState.canShare) {
            return;
        }
        try {
            await navigator.clipboard.writeText(waMeUrl);
            onNotice(labels.linkCopied);
        } catch (error) {
            onError(error);
        }
    };

    const exportCsv = () => {
        if (!sharingState.canShare || sharingState.exportRows.length === 0) {
            return;
        }
        const blob = new Blob(
            [
                buildWhatsappPreRegistrationCsv(
                    sharingState.exportRows,
                    config.regions,
                    labels
                )
            ],
            {
                type: "text/csv;charset=utf-8"
            }
        );
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = "whatsapp-pre-registration-links.csv";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(url);
    };

    return (
        <>
            <Paper elevation={0} style={{ padding: 24 }}>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    mb={2}
                    style={{ gap: 12 }}
                >
                    <Typography variant="h6">{labels.links}</Typography>
                    <Button
                        onClick={exportCsv}
                        disabled={
                            !sharingState.canShare ||
                            sharingState.exportRows.length === 0
                        }
                        variant="outlined"
                    >
                        {labels.exportCsv}
                    </Button>
                </Box>
                {actionsDisabled && (
                    <Box mb={2}>
                        <Alert severity="info">{labels.dirtyLinksNotice}</Alert>
                    </Box>
                )}
                <Box mb={2}>
                    <Typography variant="subtitle1">
                        {labels.generalEntry}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {labels.generalEntryHelp}
                    </Typography>
                    {hasUsableGeneralEntry ? (
                        <Box display="flex" alignItems="center" flexWrap="wrap">
                            <Typography variant="body2">
                                {sharingState.generalEntryUrl}
                            </Typography>
                            <Tooltip title={labels.copy}>
                                <IconButton
                                    onClick={() =>
                                        copyLink(sharingState.generalEntryUrl)
                                    }
                                    disabled={!sharingState.canShare}
                                    aria-label={labels.copy}
                                >
                                    <FileCopyIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={labels.open}>
                                <IconButton
                                    onClick={() =>
                                        window.open(
                                            sharingState.generalEntryUrl,
                                            "_blank",
                                            "noopener,noreferrer"
                                        )
                                    }
                                    disabled={!sharingState.canShare}
                                    aria-label={labels.open}
                                >
                                    <OpenInNewIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={labels.viewQr}>
                                <IconButton
                                    onClick={() =>
                                        setSelectedQrUrl(
                                            sharingState.generalEntryUrl
                                        )
                                    }
                                    disabled={!sharingState.canShare}
                                    aria-label={labels.viewQr}
                                >
                                    <VisibilityIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ) : (
                        <Alert severity="info">{labels.linkUnavailable}</Alert>
                    )}
                </Box>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{labels.store}</TableCell>
                                <TableCell>{labels.region}</TableCell>
                                <TableCell>{labels.status}</TableCell>
                                <TableCell>{labels.link}</TableCell>
                                <TableCell align="right">
                                    {labels.actions}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => {
                                const presentation =
                                    getWhatsappPreRegistrationLinkPresentation({
                                        row,
                                        regions: config.regions,
                                        canShare: sharingState.canShare,
                                        labels
                                    });
                                return (
                                    <TableRow key={row.storeId}>
                                        <TableCell>{row.storeName}</TableCell>
                                        <TableCell>
                                            {presentation.regionName}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                color={
                                                    presentation.isActive
                                                        ? "primary"
                                                        : "default"
                                                }
                                                label={presentation.statusLabel}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {presentation.shareableUrl || "-"}
                                        </TableCell>
                                        <TableCell align="right">
                                            {presentation.hasCurrentLink && (
                                                <>
                                                    <Tooltip
                                                        title={labels.copy}
                                                    >
                                                        <IconButton
                                                            onClick={() =>
                                                                copyLink(
                                                                    presentation.shareableUrl
                                                                )
                                                            }
                                                            disabled={
                                                                !sharingState.canShare
                                                            }
                                                            aria-label={
                                                                labels.copy
                                                            }
                                                        >
                                                            <FileCopyIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip
                                                        title={labels.open}
                                                    >
                                                        <IconButton
                                                            onClick={() =>
                                                                window.open(
                                                                    presentation.shareableUrl,
                                                                    "_blank",
                                                                    "noopener,noreferrer"
                                                                )
                                                            }
                                                            disabled={
                                                                !sharingState.canShare
                                                            }
                                                            aria-label={
                                                                labels.open
                                                            }
                                                        >
                                                            <OpenInNewIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip
                                                        title={labels.viewQr}
                                                    >
                                                        <IconButton
                                                            onClick={() =>
                                                                setSelectedQrUrl(
                                                                    presentation.shareableUrl
                                                                )
                                                            }
                                                            disabled={
                                                                !sharingState.canShare
                                                            }
                                                            aria-label={
                                                                labels.viewQr
                                                            }
                                                        >
                                                            <VisibilityIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}
                                            <Tooltip
                                                title={
                                                    actionsDisabled
                                                        ? labels.saveBeforeLinkAction
                                                        : presentation.hasCurrentLink
                                                        ? labels.rotate
                                                        : labels.generate
                                                }
                                            >
                                                <span>
                                                    <IconButton
                                                        onClick={() =>
                                                            onRotate(
                                                                row.storeId,
                                                                presentation.hasCurrentLink
                                                            )
                                                        }
                                                        disabled={
                                                            actionsDisabled ||
                                                            actingStoreId ===
                                                                row.storeId
                                                        }
                                                    >
                                                        <RefreshIcon />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            {presentation.hasActiveLinkRecord && (
                                                <Tooltip
                                                    title={
                                                        actionsDisabled
                                                            ? labels.saveBeforeLinkAction
                                                            : labels.revoke
                                                    }
                                                >
                                                    <span>
                                                        <IconButton
                                                            onClick={() =>
                                                                onRevoke(
                                                                    row.storeId
                                                                )
                                                            }
                                                            disabled={
                                                                actionsDisabled ||
                                                                actingStoreId ===
                                                                    row.storeId
                                                            }
                                                        >
                                                            <BlockIcon />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5}>
                                        <Alert severity="info">
                                            {labels.noLinks}
                                        </Alert>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog
                open={Boolean(sharingState.selectedQrUrl)}
                onClose={() => setSelectedQrUrl("")}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>{labels.qrTitle}</DialogTitle>
                <DialogContent>
                    <Box display="flex" justifyContent="center" py={2}>
                        {sharingState.selectedQrUrl && (
                            <ServerUrlQrCode
                                value={sharingState.selectedQrUrl}
                                size={240}
                                level="M"
                                includeMargin
                            />
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSelectedQrUrl("")}>
                        {labels.close}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default WhatsappPreRegistrationLinks;
